import { useState, useRef, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { searchStops, type StopResult } from '@/services/stop-search';
import { planTrip, type Journey, type JourneyLeg } from '@/services/trip-planner';
import { addSavedTrip } from '@/store/favourites';
import './TripPlannerScreen.css';

const MODE_COLORS: Record<string, string> = {
  Train: '#F6891F', Bus: '#0072C6', Ferry: '#61B255', Metro: '#0099CD', 'Light Rail': '#D81E34', Walk: '#9E9E9E', Transit: '#9E9E9E',
};
function fmtTime(iso: string): string { if (!iso) return '—'; return new Date(iso).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }); }
function fmtDur(s: number): string { if (!s || isNaN(s) || s < 0) return '—'; const h = Math.floor(s / 3600); const m = Math.floor((s % 3600) / 60); return h > 0 ? `${h}h ${m}m` : m > 0 ? `${m} min` : '<1 min'; }

function StopSearchInput({ id, label, placeholder, value, disabledId, onChange, onSelect }: { id: string; label: string; placeholder: string; value: string; disabledId?: string; onChange: (v: string) => void; onSelect: (stop: StopResult) => void }) {
  const [results, setResults] = useState<StopResult[]>([]); const [open, setOpen] = useState(false); const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null); const abortRef = useRef<AbortController | null>(null);
  useEffect(() => { function h(e: MouseEvent) { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); } document.addEventListener('mousedown', h); return () => document.removeEventListener('mousedown', h); }, []);
  const doSearch = useCallback(async (q: string) => { if (abortRef.current) abortRef.current.abort(); if (!q || q.length < 2) { setResults([]); setOpen(false); return; } setLoading(true); const ac = new AbortController(); abortRef.current = ac; try { const s = await searchStops(q, ac.signal); const filtered = disabledId ? s.filter(x => x.id !== disabledId).slice(0, 8) : s.slice(0, 8); setResults(filtered); setOpen(true); } catch { setOpen(false); } finally { setLoading(false); } }, [disabledId]);
  return (
    <div className="planner-field" ref={ref}>
      <label htmlFor={id} className="planner-label">{label}</label>
      <div className="planner-autocomplete">
        <input id={id} type="text" className="planner-input" placeholder={placeholder} value={value} onChange={e => { onChange(e.target.value); doSearch(e.target.value); }} autoComplete="off" spellCheck={false} />
        {loading && <span className="planner-spinner" />}
        {open && results.length > 0 && (<ul className="planner-dropdown" role="listbox">{results.map(s => (<li key={s.id} role="option" className="planner-dropdown-item" onClick={() => { onChange(s.name); onSelect(s); setOpen(false); }}><span className="planner-dropdown-name">{s.name}{s.parentName && <span className="planner-dropdown-parent"> — {s.parentName}</span>}</span></li>))}</ul>)}
      </div>
    </div>
  );
}

type SortMode = 'fastest' | 'leastTransfers' | 'leastWalking';

export function TripPlannerScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromParam = searchParams.get('from'); const toParam = searchParams.get('to');
  const fromNameParam = searchParams.get('fromName'); const toNameParam = searchParams.get('toName');

  // Restore persisted form state from sessionStorage (survives back-navigation)
  const now = new Date();
  const persisted = (() => { try { return JSON.parse(sessionStorage.getItem('tripForm') || 'null'); } catch { return null; } })();
  const todayStr = now.toISOString().split('T')[0];
  const timeStr = `${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;

  const [origin, setOrigin] = useState(fromNameParam || persisted?.origin || '');
  const [originId, setOriginId] = useState(fromParam || persisted?.originId || '');
  const [destination, setDestination] = useState(toNameParam || persisted?.destination || '');
  const [destId, setDestId] = useState(toParam || persisted?.destId || '');
  const [depArr, setDepArr] = useState<'dep' | 'arr'>(persisted?.depArr || 'dep');
  const [date, setDate] = useState(persisted?.date || todayStr);
  const [time, setTime] = useState(persisted?.time || timeStr);
  const [journeys, setJourneys] = useState<Journey[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sortMode, setSortMode] = useState<SortMode>('fastest');
  const [savedMsg, setSavedMsg] = useState('');

  // Persist form fields to sessionStorage whenever they change
  useEffect(() => {
    sessionStorage.setItem('tripForm', JSON.stringify({ origin, originId, destination, destId, depArr, date, time }));
  }, [origin, originId, destination, destId, depArr, date, time]);

  const handleSearch = async () => {
    if (!originId || !destId) return;
    setLoading(true); setError(''); setJourneys([]);
    try {
      const dt = `${date}T${time}:00`;
      console.log('planTrip call:', originId, destId, dt, depArr);
      const results = await planTrip(originId, destId, dt, depArr === 'dep');
      console.log('planTrip results:', results.length);
      if (results.length === 0) setError('No journeys found.');
      setJourneys(results);
    } catch (err) { setError(`Failed: ${err instanceof Error ? err.message : 'Unknown'}`); }
    finally { setLoading(false); }
  };

  // Auto-search when both stop IDs are available (from URL params or restored state)
  const didAutoSearch = useRef(false);
  useEffect(() => {
    if (!didAutoSearch.current && originId && destId && (origin || fromNameParam) && (destination || toNameParam)) {
      didAutoSearch.current = true;
      handleSearch();
    }
  }, [originId, destId, origin, destination, fromNameParam, toNameParam]);

  // Sort journeys
  const sorted = [...journeys].sort((a, b) => {
    if (sortMode === 'fastest') return a.duration - b.duration;
    if (sortMode === 'leastTransfers') return a.transfers - b.transfers;
    // leastWalking: total walking distance
    const walkA = a.legs.filter(l => l.mode === 'walk').reduce((s, l) => s + (l.distance || 0), 0);
    const walkB = b.legs.filter(l => l.mode === 'walk').reduce((s, l) => s + (l.distance || 0), 0);
    return walkA - walkB;
  });

  const swap = () => { setOrigin(destination); setDestination(origin); setOriginId(destId); setDestId(originId); };

  return (
    <main className="page-container">
      <h1 className="page-title">Trip Planner</h1>
      <div className="card planner-card">
        <div className="planner-inputs">
          <StopSearchInput id="origin" label="From" placeholder="Station, stop, or address..." value={origin} onChange={setOrigin} disabledId={destId} onSelect={s => setOriginId(s.id)} />
          <button className="planner-swap" onClick={swap} type="button">⇅</button>
          <StopSearchInput id="destination" label="To" placeholder="Station, stop, or address..." value={destination} onChange={setDestination} disabledId={originId} onSelect={s => setDestId(s.id)} />
        </div>

        {/* Time/Date + Depart/Arrive */}
        <div className="planner-time-row">
          <div className="planner-deparr">
            <button className={`dabtn${depArr === 'dep' ? ' dabtn--active' : ''}`} onClick={() => setDepArr('dep')}>Depart at</button>
            <button className={`dabtn${depArr === 'arr' ? ' dabtn--active' : ''}`} onClick={() => setDepArr('arr')}>Arrive by</button>
          </div>
          <input className="planner-date" type="date" value={date} onChange={e => setDate(e.target.value)} />
          <input className="planner-time" type="time" value={time} onChange={e => setTime(e.target.value)} />
        </div>

        <div className="planner-actions">
          <button className="planner-submit" disabled={!originId || !destId || loading} onClick={handleSearch}>
            {loading ? 'Searching...' : 'Find Journeys'}
          </button>
          {sorted.length > 0 && (
            <button className="planner-save" onClick={() => { addSavedTrip({ name: `${origin||originId} → ${destination||destId}`, originId, originName: origin||originId, destId, destName: destination||destId }); setSavedMsg('Trip saved!'); setTimeout(() => setSavedMsg(''), 2000); }}>💾 Save</button>
          )}
        </div>
        {savedMsg && <div className="planner-saved-msg">{savedMsg}</div>}

        {/* Sort picker */}
        {sorted.length > 0 && (
          <div className="planner-sort">
            <span className="planner-sort-label">Sort:</span>
            {(['fastest','leastTransfers','leastWalking'] as SortMode[]).map(m => (
              <button key={m} className={`route-pill${sortMode === m ? ' route-pill--active' : ''}`} onClick={() => setSortMode(m)}>
                {m === 'fastest' ? '⚡ Fastest' : m === 'leastTransfers' ? '🔄 Fewest transfers' : '🚶 Least walking'}
              </button>
            ))}
          </div>
        )}
      </div>

      {error && <div className="planner-error">{error}</div>}

      {sorted.length > 0 && (
        <div className="journey-results">
          {/* Journey cards — all shown, clickable */}
          {sorted.slice(0, 5).map((j, i) => (
            <button key={i} className="card journey-card-clickable" onClick={() => navigate(`/journey?idx=${i}`, { state: { journeys: sorted, index: i, origin: origin || originId, dest: destination || destId } })}>
              <div className="journey-card-top">
                <span className="journey-card-times">{fmtTime(j.departure)} → {fmtTime(j.arrival)}</span>
                <span className="journey-card-dur">{fmtDur(j.duration)}</span>
              </div>
              <div className="journey-card-meta">
                <span>{j.transfers} transfer{j.transfers !== 1 ? 's' : ''}</span>
                <span className="journey-card-modes">
                  {j.legs.map((l, li) => {
                    const letter = l.modeName === 'Train' ? 'T' : l.modeName === 'Bus' ? 'B' : l.modeName === 'Ferry' ? 'F' : l.modeName === 'Metro' ? 'M' : l.modeName === 'Light Rail' ? 'L' : null;
                    const color = MODE_COLORS[l.modeName] || '#9E9E9E';
                    if (letter) {
                      return <span key={li} className="jmode-badge" style={{ background: color }}>{letter}</span>;
                    }
                    return null;
                  })}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}

      {!loading && !error && sorted.length === 0 && (
        <div className="placeholder-screen"><span className="icon">🧭</span><p className="label">Enter an origin and destination to plan your trip</p></div>
      )}
    </main>
  );
}


